import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import SectionTrustBar from '../../components/feature/SectionTrustBar';
import SectionAIAnswer from '../../components/feature/SectionAIAnswer';
import SectionProcess from '../../components/feature/SectionProcess';
import SectionServiceAreas from '../../components/feature/SectionServiceAreas';
import SectionTestimonials from '../../components/feature/SectionTestimonials';
import SectionFAQ from '../../components/feature/SectionFAQ';
import SectionFinalCTA from '../../components/feature/SectionFinalCTA';

const trustItems = [
  { value: '20', label: 'Acres of Property', icon: 'ri-landscape-line' },
  { value: 'TX', label: 'Family-Owned & Operated', icon: 'ri-home-heart-line' },
  { value: '1', label: 'Event Per Day', icon: 'ri-heart-line' },
  { value: '300', label: 'Guest Capacity', icon: 'ri-group-line' },
];

const aiAnswers = [
  {
    question: 'Who owns and operates Red Door Ranch Venue?',
    answer:
      "Red Door Ranch is owned and operated by Tracy and Vernon Chapa, a couple who turned a lifelong dream of hospitality into reality. After retiring, they purchased 20 acres in Joshua, TX and built the venue from the ground up — pouring their hearts into every detail to create a space where others could celebrate life's greatest moments.",
  },
  {
    question: 'What is the story behind Red Door Ranch?',
    answer:
      "The Chapas dreamed for years of owning a bed and breakfast and being part of the hospitality industry. That dream evolved after hosting their daughter's wedding reception — an experience so rewarding it inspired the idea of an event venue. They sold their home in Arlington, purchased the Joshua property, and built Red Door Ranch as a labor of love.",
  },
  {
    question: 'What makes Red Door Ranch different from other event venues?',
    answer:
      "What sets Red Door Ranch apart is the deeply personal investment its owners bring to every event. Tracy and Vernon built this venue not as a business transaction, but as an extension of their family values. Every couple and client receives the same care they would want for their own family — making your dream come true is their purpose.",
  },
  {
    question: 'How does Red Door Ranch approach the booking experience?',
    answer:
      "Every journey begins with a personal tour of the 20-acre estate with Tracy or Vernon themselves. From there, the team works closely with you to design an event that perfectly reflects your vision. Their one-event-per-day policy ensures your celebration is never rushed or compromised.",
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Dream It',
    description: 'Every extraordinary event begins with a vision. Share yours with us.',
  },
  {
    number: '02',
    title: 'Visit Us',
    description: 'Experience the property firsthand — walk the grounds, feel the atmosphere.',
  },
  {
    number: '03',
    title: 'Build It Together',
    description: 'We collaborate to design an event that is uniquely, perfectly yours.',
  },
  {
    number: '04',
    title: 'Live It',
    description: 'On your day, everything comes together — beautifully and effortlessly.',
  },
];

const serviceAreas = [
  { city: 'Joshua, TX', note: 'Venue Location' },
  { city: 'Fort Worth, TX', note: '20 min north' },
  { city: 'Burleson, TX' },
  { city: 'Cleburne, TX' },
  { city: 'Mansfield, TX' },
  { city: 'Crowley, TX' },
  { city: 'Alvarado, TX' },
  { city: 'Granbury, TX' },
  { city: 'Arlington, TX' },
  { city: 'Dallas, TX' },
];

const aboutFAQs = [
  {
    question: 'Who are the owners of Red Door Ranch Venue in Joshua TX?',
    answer:
      'Red Door Ranch is proudly owned and operated by Tracy and Vernon Chapa, a family-owned couple based in Joshua, TX. They built the 20-acre venue from their personal dream of creating a hospitality destination where every event is treated like a family celebration — with genuine care, warmth, and attention to detail.',
  },
  {
    question: 'Is Red Door Ranch a family-owned wedding venue?',
    answer:
      'Yes. Red Door Ranch is a family-owned and family-operated luxury event venue in Joshua, TX. Owners Tracy and Vernon Chapa are personally invested in every event, ensuring the level of warmth, professionalism, and care that can only come from those who truly love what they do.',
  },
  {
    question: 'What is the philosophy behind Red Door Ranch?',
    answer:
      'The venue was built around a simple but powerful belief: that your most important moments deserve a setting that inspires awe and a team that treats your celebration like their own. Red Door Ranch exists to make your dream come true — just as the Chapas made theirs.',
  },
  {
    question: 'How long has Red Door Ranch been open as a venue?',
    answer:
      'Red Door Ranch has been welcoming couples, families, and corporate clients as a premier event venue in Joshua, TX for several years. The Chapas built the property from scratch on their 20-acre hilltop estate, crafting every detail to create a world-class luxury experience for the DFW community.',
  },
];

function AboutStory() {
  const { ref: leftRef, isVisible: leftVisible } = useInView();
  const { ref: rightRef, isVisible: rightVisible } = useInView({ threshold: 0.1 });
  const { ref: valRef, isVisible: valVisible } = useInView({ threshold: 0.05 });

  return (
    <section className="bg-white py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start mb-24">
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`slide-left ${leftVisible ? 'is-visible' : ''}`}
          >
            <div className="inline-flex items-center gap-4 mb-5">
              <span className="gold-divider" />
              <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">Meet the Owners</p>
            </div>
            <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl mb-8 leading-tight">
              Tracy &amp; Vernon Chapa
            </h2>
            <p className="text-rdr-muted font-sans text-base leading-relaxed mb-6">
              Most clients have a common reaction when they enter Red Door Ranch — &ldquo;WOW!&rdquo;
              That initial response is exactly how we still react when we walk through our doors.
            </p>
            <p className="text-rdr-muted font-sans text-base leading-relaxed mb-6">
              For years we dreamed and planned for this life. When we retired, we wanted to be involved
              in the hospitality industry — originally envisioning a B&amp;B. But when our youngest daughter
              married and we hosted her reception, everything changed. The joy of creating that experience
              for her inspired us to pursue something greater.
            </p>
            <p className="text-rdr-muted font-sans text-base leading-relaxed mb-10">
              We purchased 20 acres in Joshua, sold our home in Arlington, and began to build — with
              our friends and family by our side every step of the way. Red Door Ranch was born from
              that love. And in turn, we are here to make your dream come true.
            </p>
            <blockquote className="relative border-l-2 border-rdr-gold pl-8 py-2 mb-10">
              <p className="font-serif italic text-rdr-charcoal text-xl lg:text-2xl leading-snug">
                &ldquo;We are here to make your dream come true.&rdquo;
              </p>
              <cite className="text-rdr-gold font-sans text-xs tracking-widest uppercase mt-3 block not-italic">
                — Tracy &amp; Vernon Chapa, Owners
              </cite>
            </blockquote>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/location#contact"
                className="inline-flex items-center gap-3 bg-rdr-gold text-white font-sans text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule a Visit
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-3 border border-rdr-charcoal/30 text-rdr-charcoal font-sans text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-rdr-gold hover:text-rdr-gold transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                View Our Services
              </Link>
            </div>
          </div>
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`slide-right ${rightVisible ? 'is-visible' : ''}`}
          >
            <div className="relative overflow-hidden w-full h-[500px] lg:h-[620px] img-hover-zoom">
              <img
                src="https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/1bd30076-826e-42be-9ab9-8645d6e03a49/RDR+T%26V.jpeg"
                alt="Tracy and Vernon Chapa owners of Red Door Ranch venue Joshua TX"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark/20 to-transparent" />
            </div>
          </div>
        </div>

        {/* Values */}
        <div
          ref={valRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {[
            {
              icon: 'ri-heart-2-line',
              title: 'Built With Purpose',
              desc: 'Every inch of Red Door Ranch was designed intentionally — from the grand staircase to the bridal suites — to give your event the setting it truly deserves.',
            },
            {
              icon: 'ri-hand-heart-line',
              title: 'Hosted With Care',
              desc: 'As a family-owned venue, we treat every event with the personal investment and warmth that only people who truly love what they do can provide.',
            },
            {
              icon: 'ri-star-smile-line',
              title: 'Remembered Forever',
              desc: 'Our mission is simple: to create experiences so beautiful and memorable that you and your guests will talk about them for a lifetime.',
            },
          ].map((v, i) => (
            <div
              key={i}
              className={`bg-rdr-cream border border-rdr-cream-dark p-10 fade-up ${valVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              <div className="w-11 h-11 flex items-center justify-center border border-rdr-gold/40 bg-rdr-gold/5 mb-6">
                <i className={`${v.icon} text-xl text-rdr-gold`} />
              </div>
              <h3 className="font-serif text-rdr-charcoal text-xl mb-4">{v.title}</h3>
              <p className="text-rdr-muted font-sans text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutWhyChooseUs() {
  const { ref, isVisible } = useInView({ threshold: 0.1 });

  return (
    <section className="bg-rdr-charcoal py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 fade-up ${isVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">Our Promise</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-white text-4xl lg:text-5xl xl:text-6xl max-w-2xl mx-auto mb-6 leading-tight">
            An Unforgettable Experience From Start to Finish
          </h2>
          <p className="text-white/50 font-sans text-base leading-relaxed max-w-xl mx-auto">
            We host only one event per day — so your celebration is never rushed, never compromised,
            and always given the full attention it deserves.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: 'ri-calendar-check-line', label: 'Exclusive Daily Access' },
            { icon: 'ri-map-2-line', label: '20-Acre Private Estate' },
            { icon: 'ri-group-line', label: 'Up to 300 Guests' },
            { icon: 'ri-home-smile-line', label: 'Family-Owned & Operated' },
          ].map((item, i) => (
            <div
              key={i}
              className={`bg-white/5 border border-white/10 p-8 text-center hover:border-rdr-gold/40 transition-colors duration-300 fade-up ${isVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
                <i className={`${item.icon} text-2xl text-rdr-gold`} />
              </div>
              <p className="font-serif text-white text-base leading-snug">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <main>
      {/* 1. Hero */}
      <section className="relative min-h-screen flex items-end justify-center overflow-hidden grain-overlay">
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/f9f3d3507cd45353c13229ad68f01b75.jpg"
            alt="Red Door Ranch luxury venue entrance Joshua TX"
            className="w-full h-full object-cover object-center scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-rdr-dark via-rdr-dark/35 to-rdr-dark/15" />
          <div className="absolute inset-0 bg-gradient-to-r from-rdr-dark/30 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32 pt-36">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-8 hero-eyebrow">
              <span className="block w-12 h-px bg-rdr-gold" />
              <p className="text-rdr-gold text-xs tracking-[0.35em] uppercase font-sans font-medium">Our Story</p>
            </div>
            <h1 className="font-serif text-white text-5xl lg:text-6xl xl:text-[4.5rem] leading-[1.04] mb-8 hero-headline">
              Built With Love.<br />
              <em className="italic text-rdr-gold">Made for Your Dream.</em>
            </h1>
            <p className="text-white/70 font-sans text-lg max-w-xl mb-12 leading-relaxed hero-sub">
              Red Door Ranch is the fulfillment of a lifelong dream by Tracy &amp; Vernon Chapa —
              and they&apos;re here to help you fulfill yours.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 hero-cta">
              <Link
                to="/location#contact"
                className="inline-flex items-center gap-3 bg-rdr-gold text-white font-sans text-xs tracking-[0.25em] uppercase px-10 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule a Visit
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-sans text-xs tracking-[0.25em] uppercase px-10 py-4 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                View Services
              </Link>
            </div>
          </div>
          {/* Stats strip */}
          <div className="hero-cta mt-16 lg:mt-20 pt-8 border-t border-white/10 flex flex-wrap items-center gap-8 lg:gap-14">
            {[
              { value: '20', label: 'Private Acres' },
              { value: '300', label: 'Guest Capacity' },
              { value: '1', label: 'Event Per Day' },
              { value: '100%', label: 'Family Owned' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-4">
                <span className="font-serif text-white text-3xl font-medium">{s.value}</span>
                <div className="w-px h-8 bg-white/15" />
                <span className="text-white/45 font-sans text-[11px] tracking-[0.2em] uppercase">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <SectionTrustBar items={trustItems} />
      {/* 3. About Story + Values */}
      <AboutStory />
      {/* 4. AI Answer Block */}
      <SectionAIAnswer heading="About Red Door Ranch" items={aiAnswers} />
      {/* 5. Process */}
      <SectionProcess heading="How It All Comes Together" steps={processSteps} />
      {/* 6. Why Choose Us */}
      <AboutWhyChooseUs />
      {/* 7. Service Areas */}
      <SectionServiceAreas areas={serviceAreas} />
      {/* 8. Testimonials */}
      <SectionTestimonials />
      {/* 9. FAQ */}
      <SectionFAQ heading="About the Venue" items={aboutFAQs} />
      {/* 10. Final CTA */}
      <SectionFinalCTA
        heading="Let&apos;s Create Something Extraordinary"
        bgImage="https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/1631653889244-4L86Y1JQ4I8PA1YERL3S/FA606739-06AC-4820-A84F-631482186EA5.jpeg"
      />
    </main>
  );
}
